import React, { useEffect, useRef, useState } from 'react';
import { UserPlus, Search, MessageSquare, DollarSign, Building2, Users, Mail, CheckCircle, ArrowRight } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import SectionHeader from './SectionHeader';

const HowItWorks = () => {
  const sectionRef = useRef(null);
  const [activeTab, setActiveTab] = useState('freelancer');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const freelancerSteps = [
    {
      step: '01',
      icon: UserPlus,
      title: 'Create Your Profile',
      description: 'Sign up in minutes and build a stunning professional profile. Add your portfolio, skills, and experience to stand out.',
      color: 'from-blue-500 to-cyan-400',
      bgColor: 'bg-white/85 dark:bg-slate-900/70 backdrop-blur-sm'
    },
    {
      step: '02',
      icon: Search,
      title: 'Discover Perfect Jobs',
      description: 'Browse thousands of opportunities with smart AI matching. Find projects that align with your skills and interests.',
      color: 'from-cyan-500 to-teal-400',
      bgColor: 'bg-white/85 dark:bg-slate-900/70 backdrop-blur-sm'
    },
    {
      step: '03',
      icon: MessageSquare,
      title: 'Apply & Connect',
      description: 'Send winning proposals and chat directly with clients. Build relationships and negotiate terms seamlessly.',
      color: 'from-teal-500 to-green-400',
      bgColor: 'bg-white/85 dark:bg-slate-900/70 backdrop-blur-sm'
    },
    {
      step: '04',
      icon: DollarSign,
      title: 'Get Paid Securely',
      description: 'Complete projects with confidence and receive payments securely. Track earnings and grow your freelance career.',
      color: 'from-green-500 to-emerald-400',
      bgColor: 'bg-white/85 dark:bg-slate-900/70 backdrop-blur-sm'
    }
  ];

  const clientSteps = [
    {
      step: '01',
      icon: Building2,
      title: 'Post Your Job',
      description: 'Create detailed job listings with requirements, budget, and timeline. Reach thousands of talented freelancers instantly.',
      color: 'from-purple-500 to-pink-400',
      bgColor: 'bg-white/85 dark:bg-slate-900/70 backdrop-blur-sm'
    },
    {
      step: '02',
      icon: Users,
      title: 'Review Top Talent',
      description: 'Browse verified freelancer profiles with portfolios, ratings, and reviews. Find the perfect match for your project.',
      color: 'from-pink-500 to-rose-400',
      bgColor: 'bg-white/85 dark:bg-slate-900/70 backdrop-blur-sm'
    },
    {
      step: '03',
      icon: Mail,
      title: 'Hire & Collaborate',
      description: 'Select your ideal candidate and start collaborating. Use built-in chat and project management tools.',
      color: 'from-rose-500 to-red-400',
      bgColor: 'bg-white/85 dark:bg-slate-900/70 backdrop-blur-sm'
    },
    {
      step: '04',
      icon: CheckCircle,
      title: 'Launch Successfully',
      description: 'Monitor progress, provide feedback, and complete projects with ease. Build long-term partnerships.',
      color: 'from-amber-500 to-orange-400',
      bgColor: 'bg-white/85 dark:bg-slate-900/70 backdrop-blur-sm'
    }
  ];

  const steps = activeTab === 'freelancer' ? freelancerSteps : clientSteps;

  return (
    <section
      id="how-it-works"
      className="py-12 md:py-12 relative overflow-hidden"
      ref={sectionRef}
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-0 left-0 h-80 w-80 rounded-full bg-blue-500/10 blur-[120px]"></div>
        <div className="absolute bottom-0 right-0 h-80 w-80 rounded-full bg-cyan-500/10 blur-[120px]"></div>
      </div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <SectionHeader
          eyebrow="How It Works"
          title="Get Started in"
          accent="4 Simple Steps"
          description="Join thousands of successful freelancers and clients on Kaamhub."
          className="mb-12 md:mb-14"
        />

        {/* Tab Switcher */}
        <div className="mb-12 flex justify-center md:mb-14">
          <div className="inline-flex rounded-2xl border border-slate-200/80 bg-white/85 p-2 gap-2 shadow-lg dark:border-white/10 dark:bg-slate-900/80 backdrop-blur-sm">
            <button
              onClick={() => setActiveTab('freelancer')}
              className={`px-8 py-4 rounded-xl font-semibold transition-all duration-300 ${
                activeTab === 'freelancer'
                  ? 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white shadow-lg'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              For Freelancers
            </button>
            <button
              onClick={() => setActiveTab('client')}
              className={`px-8 py-4 rounded-xl font-semibold transition-all duration-300 ${
                activeTab === 'client'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white shadow-lg'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              For Clients
            </button>
          </div>
        </div>

        {/* Steps Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {steps.map((step, index) => {
            const IconComponent = step.icon;
            return (
              <Card
                key={index}
                className={`group relative overflow-hidden border border-slate-200/80 dark:border-white/10 hover:border-blue-200 dark:hover:border-cyan-800 transition-all duration-500 transform hover:-translate-y-2 hover:shadow-2xl ${step.bgColor}`}
              >
                {/* Step Number Background */}
                <div className="absolute -top-6 -right-6 text-8xl font-bold text-gray-200/20 dark:text-gray-800/20">
                  {step.step}
                </div>

                <CardContent className="p-8 relative z-10">
                  {/* Icon */}
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center mb-6 transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-xl`}>
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>

                  {/* Step Number */}
                  <div className={`text-sm font-bold text-transparent bg-clip-text bg-gradient-to-r ${step.color} mb-2`}>
                    STEP {step.step}
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-blue-600 dark:group-hover:text-cyan-400 transition-colors">
                    {step.title}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                    {step.description}
                  </p>

                  {/* Arrow indicator for flow */}
                  {index < steps.length - 1 && (
                    <div className="hidden lg:block absolute -right-3 top-1/2 transform -translate-y-1/2">
                      <ArrowRight className="w-6 h-6 text-blue-400 dark:text-cyan-400" />
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Call to Action */}
        <div className="pt-2 text-center">
          <button
            onClick={() => document.getElementById('download').scrollIntoView({ behavior: 'smooth' })}
            className={`inline-flex items-center px-10 py-5 bg-gradient-to-r ${
              activeTab === 'freelancer'
                ? 'from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600'
                : 'from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600'
            } text-white text-lg font-semibold rounded-2xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300`}
          >
            {activeTab === 'freelancer' ? 'Start Freelancing Today' : 'Start Hiring Today'}
            <ArrowRight className="ml-2 w-5 h-5" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
